import React from 'react';
import styled from '@emotion/styled';
import { Only } from 'ui/components/elements/Only/Only';
import IssueIcon from 'ui/images/top/topIssue_icon.svg';
import IssueCircleIcon from 'ui/images/top/topIssue_circle.svg';
import IssueImg01 from 'ui/images/top/topIssue_img01@2x.png';
import IssueImg02 from 'ui/images/top/topIssue_img02@2x.png';
import BalloonImgSp from 'ui/images/top/topIssue_fukidashi_sp.svg';
import BalloonImgPc from 'ui/images/top/topIssue_fukidashi_pc.svg';
import IssueImg03Sp from 'ui/images/top/topIssue_img03_sp@2x.png';
import IssueImg03Pc from 'ui/images/top/topIssue_img03_pc@2x.png';
import { mqMin } from 'utils/responsive';

const Container = styled.section`
    padding: 86px 0 70px 0;
    background-color: #e7e7e7;
    *,
    *::before,
    *::after {
        box-sizing: border-box;
    }

    ${mqMin('NARROW')} {
        padding: 79px 0 68px 0;
    }
`;

const H2 = styled.h2`
    text-align: center;
    font-size: 1.5rem;
    line-height: 1.8em;
    letter-spacing: 0.05em;
    margin-bottom: 32px;

    strong {
        position: relative;
        font-size: 1.75rem; /*28px*/

        &::after {
            position: absolute;
            top: -35px;
            left: 60%;
            content: '';
            display: block;
            width: 34px;
            height: 34px;
            background-image: url(${IssueIcon});
            background-repeat: no-repeat;
            background-size: contain;
            background-position: auto;
        }
    }

    ${mqMin('SP')} {
        font-size: 2rem;
        strong {
            font-size: 2.3rem;
        }
    }

    ${mqMin('NARROW')} {
        font-size: 1rem;
        letter-spacing: 0.1em;

        strong {
            font-size: 2rem; /*32px*/
        }
    }
`;

const ExampleContainer = styled.div`
    margin-bottom: 62px;

    ${mqMin('NARROW')} {
        display: flex;
        justify-content: center;
        margin-bottom: 72px;
    }
`;

const ExampleItem = styled.div`
    position: relative;
    width: 93.02325581395349%;
    max-width: 400px;
    border-radius: 11px;
    background-color: #fff;
    margin: 0 auto 0 auto;

    ${mqMin('SP')} {
        width: 35rem;
        max-width: initial;
    }

    ${mqMin('NARROW')} {
        width: 382px;
        margin: initial;
        margin: 0 10px;
    }
`;

const ExampleItem01 = styled(ExampleItem)`
    padding: 20px 68px 20px 15px;
    margin-bottom: 20px;

    ${mqMin('NARROW')} {
        padding: 20px 15px 20px 60px;
        margin-bottom: 0;
    }
`;

const ExampleItem02 = styled(ExampleItem)`
    padding: 20px 15px 20px 68px;

    ${mqMin('NARROW')} {
        padding: 20px 20px 20px 26px;
    }
`;

const ExampleItemText = styled.div`
    line-height: 1.5em;
    letter-spacing: 0.1em;
    font-size: 0.7rem;

    ${mqMin('SP')} {
        font-size: 1.4rem;
    }

    ${mqMin('NARROW')} {
        line-height: 1.7em;
        font-size: 0.8rem;
    }
`;

const IssueImg = styled.img`
    position: absolute;
    bottom: -5px;
    width: 72px;
    height: auto;

    ${mqMin('NARROW')} {
        position: absolute;
        top: -40px;
        bottom: auto;
        width: 112px;
        height: auto;
    }
`;

const IssueImg01Item = styled(IssueImg)`
    right: -5px;

    ${mqMin('NARROW')} {
        left: -70px;
        right: auto;
    }
`;

const IssueImg02Item = styled(IssueImg)`
    left: -5px;

    ${mqMin('NARROW')} {
        left: auto;
        right: -70px;
    }
`;

const Balloon = styled.div`
    width: 146px;
    height: 60px;
    background-image: url(${BalloonImgSp});
    background-repeat: no-repeat;
    background-size: contain;
    text-align: center;
    color: #fff;
    line-height: 47px;
    letter-spacing: 0.1em;
    margin: 0 auto 10px auto;
    font-size: 0.9rem;

    ${mqMin('SP')} {
        font-size: 1.4rem;
    }

    ${mqMin('NARROW')} {
        width: 146px;
        height: 67px;
        background-image: url(${BalloonImgPc});
        font-size: 1.375rem; /*22px*/
        line-height: 50px;
    }
`;

const H3 = styled.h3`
    text-align: center;
    font-size: 1.5rem; /*24px*/
    line-height: 3.375rem;
    letter-spacing: 0.05em;
    margin-bottom: 22px;

    strong {
        font-size: 3.375rem; /*44px*/
        color: var(--blue);
    }

    span {
        color: var(--blue);
    }

    ${mqMin('SP')} {
        font-size: 2rem;
        strong {
            font-size: 4.5rem;
        }
    }

    ${mqMin('NARROW')} {
        letter-spacing: 0.1em;
        margin-bottom: 12px;

        strong {
            font-size: 3.375rem; /*54px*/
            letter-spacing: 0.05em;
        }
    }
`;

const H4 = styled.h4`
    text-align: center;
    line-height: 1.6em;
    letter-spacing: 0.1em;
    margin-bottom: 25px;

    strong {
        font-size: 1.25rem; /*20px*/
        letter-spacing: 0.05em;
    }

    ${mqMin('SP')} {
        font-size: 1.4rem;

        strong {
            font-size: 2rem;
        }
    }
    ${mqMin('NARROW')} {
        font-size: 1rem;
        strong {
            font-size: 1.25rem;
        }
    }
`;

const IssueItem02 = styled.div`
    ${mqMin('SP')} {
        font-size: 1.4rem;
    }
    ${mqMin('NARROW')} {
        display: flex;
        width: 95%;
        max-width: 1100px;
        margin: 0 auto 0 auto;
        font-size: 1rem;
    }
`;

const IssueItem02ContainerChild = styled.div`
    width: 88.37209302325581%;
    max-width: 380px;
    margin: 0 auto 0 auto;

    ${mqMin('NARROW')} {
        width: 361px;
        max-width: initial;
        margin: 0 auto 0 auto;
    }
`;

const IssueItem02Child = styled.div`
    background-color: #fff;
    border-radius: 11px;
    line-height: 1.6em;
    letter-spacing: 0.1em;
    padding: 20px;

    width: 88.37209302325581%;
    max-width: 380px;
    margin: 0 auto 0 auto;

    strong {
        color: var(--blue);
    }

    ${mqMin('NARROW')} {
        text-align: center;
        font-size: 0.8rem;
        padding: 20px 10px;

        width: 361px;
        max-width: initial;
        margin: 0 auto 0 auto;
    }
`;

const IssueItem02ChildLeft01 = styled(IssueItem02Child)`
    width: 320px;
    margin-bottom: 15px;

    ${mqMin('NARROW')} {
        width: 255px;
        margin: 0 0 20px auto;
    }
`;

const IssueItem02ChildLeft02 = styled(IssueItem02Child)`
    width: 285px;
    margin: 0 0 0 auto;

    ${mqMin('NARROW')} {
        position: relative;
        width: 285px;
        margin: 0;

        &::after {
            position: absolute;
            top: 0;
            right: -5.3vw;
            content: '';
            display: block;
            width: 43px;
            height: 41px;
            background-image: url(${IssueCircleIcon});
            background-repeat: no-repeat;
            background-size: contain;
        }
    }
`;

const IssueItem02ChildCenter = styled.div`
    width: 340px;
    margin: 13px auto 27px auto;
    img {
        display: block;
        max-width: 100%;
    }

    ${mqMin('NARROW')} {
        width: 342px;
        margin: auto 35px 0 21px;
    }
`;

const IssueItem02ChildRight01 = styled(IssueItem02Child)`
    width: 303px;
    margin-bottom: 15px;

    ${mqMin('NARROW')} {
        width: 270px;
        margin: 21px 0 35px 0;
    }
`;

const IssueItem02ChildRight02 = styled(IssueItem02Child)`
    width: 303px;
    margin: 0 0 0 auto;

    ${mqMin('NARROW')} {
        position: relative;
        width: 270px;
        margin: 0 0 0 auto;

        &::after {
            position: absolute;
            top: 54px;
            left: -5.3vw;
            content: '';
            display: block;
            width: 43px;
            height: 41px;
            background-image: url(${IssueCircleIcon});
            background-repeat: no-repeat;
            background-size: contain;
            transform: scale(-1, 1);
        }
    }
`;

export const Issue: React.FC = () => {
    return (
        <Container>
            <H2 className="fadein fadein_y">
                こんな<strong>お悩み</strong>ありませんか？
            </H2>
            <ExampleContainer className="fadein fadein_y">
                <ExampleItem01>
                    <ExampleItemText>
                        事業譲渡を考えているけど、
                        <br />
                        どこに相談すればよいかわからない...
                    </ExampleItemText>
                    <IssueImg01Item src={IssueImg01} alt="悩みのイメージ1" />
                </ExampleItem01>

                <ExampleItem02>
                    <ExampleItemText>
                        事業承継できる会社を探しているけど、
                        <br />
                        いい仲介会社が見つからない...
                    </ExampleItemText>
                    <IssueImg02Item src={IssueImg02} alt="悩みのイメージ2" />
                </ExampleItem02>
            </ExampleContainer>
            <Balloon className="fadein fadein_y">実は...</Balloon>
            <H3 className="fadein fadein_y">
                M&A支援機関認定業者は
                <Only sp>
                    <br />
                </Only>
                <strong>2800</strong>
                <span>社</span>と膨大
            </H3>
            <H4 className="fadein fadein_y">
                契約前に比較検討する機会が少なく
                <Only sp>
                    <br />
                </Only>
                <strong>トラブル</strong>も多い
            </H4>
            <IssueItem02 className="fadein fadein_y">
                <IssueItem02ContainerChild>
                    <IssueItem02ChildLeft01>
                        最低報酬金額の提示や
                        <br />
                        想定手数料などの説明がなく、
                        <br />
                        <strong>想定以上の手数料を</strong>
                        <br />
                        <strong>支払う結果</strong>となった
                    </IssueItem02ChildLeft01>
                    <IssueItem02ChildLeft02>
                        仲介とFAの違いなど、
                        <br />
                        <strong>契約内容に関しての説明が</strong>
                        <br />
                        <strong>なされないまま契約</strong>をしてしまい、
                        <br />
                        トラブルが発生した
                    </IssueItem02ChildLeft02>
                </IssueItem02ContainerChild>

                <IssueItem02ChildCenter>
                    <picture>
                        <source media="(max-width: 1023px)" srcSet={IssueImg03Sp} />
                        <source media="(min-width: 1024px)" srcSet={IssueImg03Pc} />
                        <img src={IssueImg03Pc} alt="悩みのイメージ3" />
                    </picture>
                </IssueItem02ChildCenter>

                <IssueItem02ContainerChild>
                    <IssueItem02ChildRight01>
                        M&A実行後も<strong>個人保証が</strong>
                        <br />
                        <strong>前代表に残ったまま</strong>になっていた
                    </IssueItem02ChildRight01>
                    <IssueItem02ChildRight02>
                        企業価値の評価手法や
                        <br />
                        前提条件についての説明がなく、
                        <br />
                        <strong>納得のいかない金額で</strong>
                        <br />
                        <strong>譲渡実行させられた</strong>
                    </IssueItem02ChildRight02>
                </IssueItem02ContainerChild>
            </IssueItem02>
        </Container>
    );
};
